import { cn } from "@/lib/utils";

import { Reveal } from "./reveal";

interface SectionHeaderProps {
  tag: string;
  /** Accepts a string or JSX — use a fragment with <br /> for line breaks */
  heading: React.ReactNode;
  subheading?: string;
  className?: string;
  align?: "center" | "left";
}

/**
 * Shared section header used across every section.
 * Wraps itself in a <Reveal> so callers don't have to.
 *
 * Usage:
 *   <SectionHeader
 *     tag="How It Works"
 *     heading="Deploy Your AI Team in 3 Simple Steps"
 *     subheading="No technical skills needed."
 *   />
 *
 *   // With a line break:
 *   <SectionHeader
 *     tag="Get Started"
 *     heading={<>Every Business Is Different.<br />Let's Build Yours.</>}
 *   />
 */
export function SectionHeader({
  tag,
  heading,
  subheading,
  className,
  align = "center",
}: SectionHeaderProps) {
  return (
    <Reveal
      className={cn(
        "mb-14 max-w-155",
        align === "center" && "mx-auto text-center",
        className,
      )}
    >
      <p className="section-tag mb-3">{tag}</p>
      <h2 className="text-foreground text-[clamp(1.75rem,3.5vw,2.6rem)] leading-[1.15] font-extrabold tracking-[-0.035em]">
        {heading}
      </h2>
      {subheading && (
        <p className="text-rl-text2 mt-3 text-base leading-[1.7]">
          {subheading}
        </p>
      )}
    </Reveal>
  );
}
